/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/design-system/use-tokens-shape */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Suspense, useCallback, useState, type KeyboardEvent, type MouseEvent } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { resolveSiteHostname } from '../../../utils/site-selection';
import { selectedSiteCloudIdAtom } from '../../../utils/site-selection-storage';
import {
	availableSitesAtom,
	cloudIdAtom,
	orgIdAtom,
	previousSelectionSourceAtom,
	selectedSiteHostnameAtom,
	selectedSiteNameAtom,
} from '../../state';

import { messages } from './messages';
import { SwitchSiteItem } from './SwitchSiteItem';
import { type AvailableSite, SiteSelectionSource, SiteSwitcherCloseReason } from './types';

const styles = cssMap({
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.medium'),
		borderWidth: '1.5px',
		borderStyle: 'solid',
		borderColor: '#505258',
		backgroundColor: 'transparent',
		font: token('font.body'),
		color: '#B7B9BE',
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		// Lets the label below ellipsise instead of the trigger pushing the nav bar
		// wider than the viewport. A flex item keeps `min-width: auto` otherwise,
		// which floors it at the full width of the site name.
		minWidth: 0,
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			borderColor: '#6B6E76',
			color: '#F0F1F2',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	// A display name is arbitrarily long and never wraps, so this is the one nav
	// bar control that can push the bar past the viewport once the nav buttons
	// take their share of a phone-width bar. Shown in full wherever it fits, and
	// truncated by exactly as much as the bar is short when it does not.
	label: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

export type SiteSwitcherProps = {
	/**
	 * Initial open state of the dropdown. Defaults to `false`. Mainly
	 * useful for stories / VR snapshots that want to render the open
	 * dropdown without simulating a user click. Production callers
	 * should leave this unset.
	 */
	defaultOpen?: boolean;
};

/**
 * Site switcher trigger preserves the original microsite-themed button
 * (dark hero-on-dark border, chevron icon, etc.) so the visual treatment
 * matches the rest of the NavBar. The dropdown body is modelled on
 * townsquare's `HomeWorkspaceSwitcher` (Popup + MenuGroup/Section/HeadingItem
 * + per-site `SwitchSiteItem`) — duplicated locally so this package does
 * not depend on townsquare internals.
 */
const SiteSwitcherContent = ({ defaultOpen = false }: SiteSwitcherProps): JSX.Element | null => {
	const [cloudId, setCloudId] = useAtom(cloudIdAtom);
	const [, setOrgId] = useAtom(orgIdAtom);
	const setSelectedSiteCloudId = useSetAtom(selectedSiteCloudIdAtom);
	const setSelectedSiteName = useSetAtom(selectedSiteNameAtom);
	const setSelectedSiteHostname = useSetAtom(selectedSiteHostnameAtom);
	const [previousSelectionSource, setPreviousSelectionSource] = useAtom(
		previousSelectionSourceAtom,
	);
	const { data: sites } = useAtomValue(availableSitesAtom);
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	const [isOpen, setIsOpen] = useState(defaultOpen);

	const onClose = useCallback((): void => {
		setIsOpen((prev) => {
			if (prev) {
				fireEvent('ui.dropdown.closed.twgMicrositeSiteSwitcher', {
					closeReason: SiteSwitcherCloseReason.Dismissed,
				});
			}
			return false;
		});
	}, [fireEvent]);

	const onTriggerClick = useCallback((): void => {
		setIsOpen((prev) => {
			const next = !prev;
			if (next) {
				fireEvent('ui.dropdown.opened.twgMicrositeSiteSwitcher', {});
			} else {
				fireEvent('ui.dropdown.closed.twgMicrositeSiteSwitcher', {
					closeReason: SiteSwitcherCloseReason.Dismissed,
				});
			}
			return next;
		});
	}, [fireEvent]);

	const handleSiteClick = useCallback(
		(e: MouseEvent | KeyboardEvent, site: AvailableSite) => {
			e.preventDefault();
			const isActualChange = Boolean(site.cloudId) && site.cloudId !== cloudId;
			if (isActualChange && site.cloudId) {
				fireEvent('ui.dropdownItem.clicked.twgMicrositeSiteSwitcher', {
					previousSelectionSource,
					siteCount: sites.length,
				});
				setCloudId(site.cloudId);
				setSelectedSiteCloudId(site.cloudId);
				setSelectedSiteName(site.displayName);
				setSelectedSiteHostname(resolveSiteHostname(site));
				setPreviousSelectionSource(SiteSelectionSource.UserSelection);
			}
			if (site.org?.id) {
				setOrgId(site.org.id);
			}
			fireEvent('ui.dropdown.closed.twgMicrositeSiteSwitcher', {
				closeReason: SiteSwitcherCloseReason.ItemSelected,
			});
			setIsOpen(false);
		},
		[
			cloudId,
			fireEvent,
			previousSelectionSource,
			setCloudId,
			setOrgId,
			setSelectedSiteCloudId,
			setSelectedSiteName,
			setSelectedSiteHostname,
			setPreviousSelectionSource,
			sites.length,
		],
	);

	if (sites.length <= 1) {
		// only show the switcher if there are multiple sites
		return null;
	}

	const currentSite = sites.find(
		(site) => (site.cloudId ?? '')?.length !== 0 && site.cloudId === cloudId,
	);
	const triggerLabel = currentSite?.displayName ?? formatMessage(messages.switchSiteLabel);

	// Pin the currently selected site to the top of the list, then sort the
	// remaining sites alphabetically by displayName. `localeCompare` with
	// `sensitivity: 'base'` makes the alphabetical order locale-aware and
	// case-insensitive (so "atlassian" and "Atlassian" sort together).
	const sortedSites = [...sites].sort((a, b) => {
		if (a.cloudId === cloudId) {
			return -1;
		}
		if (b.cloudId === cloudId) {
			return 1;
		}
		return (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, {
			sensitivity: 'base',
		});
	});

	return (
		<Popup
			placement="bottom-end"
			isOpen={isOpen}
			onClose={onClose}
			shouldRenderToParent
			testId="site-switcher-menu"
			content={() => (
				<MenuGroup minWidth={192} maxWidth={480} maxHeight="80vh">
					<Section isList titleId="site-switcher-heading">
						<HeadingItem id="site-switcher-heading">
							<FormattedMessage {...messages.switchSiteHeading} />
						</HeadingItem>
						{sortedSites.map((site) => (
							<SwitchSiteItem
								key={site.cloudId ?? site.url}
								href={site.url}
								onItemClick={handleSiteClick}
								site={site}
								isSelected={(site.cloudId ?? '')?.length !== 0 && site.cloudId === cloudId}
							/>
						))}
					</Section>
				</MenuGroup>
			)}
			trigger={(triggerProps: TriggerProps) => (
				<button
					{...triggerProps}
					type="button"
					aria-label={formatMessage(messages.switchSiteLabel)}
					aria-haspopup="true"
					aria-expanded={isOpen}
					onClick={onTriggerClick}
					data-testid="site-switcher-dropdown-button"
					css={styles.trigger}
				>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<span css={styles.label}>{triggerLabel}</span>
					<ChevronDownIcon label="" color="currentColor" />
				</button>
			)}
		/>
	);
};

/**
 * The switcher owns the boundary for the suspense it causes: it is the only
 * nav-bar control that reads `availableSitesAtom`, which suspends until the
 * App Switcher `available-products` request resolves.
 *
 * Without a boundary here the suspension escapes to whichever boundary sits
 * above the nav bar, and React refuses to swap already-visible content for a
 * fallback during a click-driven update — it throws instead. Routing from
 * `/cli`, which hides the switcher, to `/` mounts it inside the click that
 * navigates, so that throw took down the whole app.
 *
 * `null` while the roster loads: the switcher already renders nothing for
 * single-site users, so the nav bar copes with it being absent.
 */
export const SiteSwitcher = (props: SiteSwitcherProps): JSX.Element => (
	<Suspense fallback={null}>
		<SiteSwitcherContent {...props} />
	</Suspense>
);
